/**
 * API Service Layer
 * Centralized API calls with error handling
 */

import config from '@config';
import { API_ENDPOINTS } from '@constants';

/**
 * Generic fetch wrapper with error handling and timeout
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>} - Parsed JSON response
 * @throws {Error} - If the request fails
 */
async function fetchWithErrorHandling(url, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.api.timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    console.error('API Error:', error);
    throw new Error(`Failed to fetch data: ${error.message}`);
  }
}

/**
 * Internal API call helper
 * @param {string} endpoint - API endpoint
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>} - Parsed JSON response
 */
export async function apiCall(endpoint, options = {}) {
  const url = `${config.api.baseUrl}${endpoint}`;
  return fetchWithErrorHandling(url, options);
}

/**
 * Convert Gregorian date to Hebrew date
 * @param {string} year - Gregorian year
 * @param {string} month - Gregorian month
 * @param {string} day - Gregorian day
 * @returns {Promise<{hebrew: string}>} - Hebrew date object
 */
export async function convertToHebrewDate(year, month, day) {
  const url = `${API_ENDPOINTS.HEBREW_DATE_CONVERTER}?cfg=json&gy=${year}&gm=${month}&gd=${day}&g2h=1`;
  try {
    const data = await fetchWithErrorHandling(url);
    console.log('Hebrew date API response:', data); // Debug log
    
    // The API returns different formats, check for hebrew or hd fields
    if (data.hebrew) {
      return { hebrew: data.hebrew };
    } else if (data.hd) {
      // If hd exists, format it properly
      const hd = data.hd;
      // hd.m is the Hebrew month name, hd.d is day, hd.y is year
      const hebrewDate = `${hd.d} ${hd.m} ${hd.y}`;
      return { hebrew: hebrewDate };
    } else if (data.hd1) {
      // Alternative format
      return { hebrew: data.hd1 };
    } else if (data.hm && data.hd && data.hy) {
      // Another possible format with separate fields
      const hebrewDate = `${data.hd} ${data.hm} ${data.hy}`;
      return { hebrew: hebrewDate };
    } else {
      console.error('Unexpected API response format:', data);
      throw new Error('Invalid response format from Hebrew date API');
    }
  } catch (error) {
    console.error('Hebrew date conversion error:', error);
    throw error;
  }
}

/**
 * Submit order to Google Sheets
 * @param {Object} orderData - Order data including customer info and cart items
 * @returns {Promise<{success: boolean, orderId?: string}>} - Success status and optional order ID
 */
export async function submitOrder(orderData) {
  try {
    // Use Google Apps Script web app URL or backend API endpoint
    const url = config.api.googleSheetsUrl || `${config.api.baseUrl}/api/orders`;
    
    const response = await fetchWithErrorHandling(url, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
    
    return response;
  } catch (error) {
    console.error('Order submission error:', error);
    throw new Error(`Failed to submit order: ${error.message}`);
  }
}

